import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { from, map, Observable, of, ReplaySubject, shareReplay, switchMap } from 'rxjs';

import { gql } from 'apollo-angular';

import { TestFileType } from 'src/enums/testFileType';
import { EcuInformation } from 'src/models/ecuInformation';
import { EcuParam } from 'src/models/ecuParam';
import { FileMetadata } from 'src/models/fileMetadata';
import { GraphqlEcuParam } from 'src/models/graphql/graphqlEcuParam';
import { GraphqlEcuReadout } from 'src/models/graphql/graphqlEcuReadout';
import { GraphqlFileMetadata } from 'src/models/graphql/graphqlFileMetadata';
import { ApolloService } from 'src/services/apollo.service';
import { ECU_PARAMETERS_FOR_TEST_RUN_QUERY, ECU_READOUTS_FOR_TEST_RUN_QUERY } from 'src/graphql/queries/abstraction-layer-queries';

/**
 * Handles file-related tasks, such as getting file metadata and file contents.
 */
@Injectable()
export class FileService {
	/**
	 * Subject containing current test run UID. Used to get files for current test run.
	 */
	private testRunSubject: ReplaySubject<string> = new ReplaySubject(1);

	/**
	 * Current test run UID. Used by database fallback queries when no S3 file exists.
	 */
	public testRunUid$ = this.testRunSubject.asObservable();

	/**
	 * Pipes current test run UID to get file metadata for input test run.
	 */
	public fileMetadata$ = this.testRunSubject.pipe(
		switchMap(testRunUid => {
			const query = gql`
				query fileHandlerExecutorGetMetadataByTestRun($TestRunUid: String!) {
					fileHandlerExecutorGetMetadataByTestRun(TestRunUid: $TestRunUid) {
						Checksum
						CreateDate
						FileName
						FileType
						FileUid
						LinkToTestItemUid
					}
				}
			`;

			return this.apolloService.abstractionLayerClient$.pipe(
				switchMap(client => {
					return client.query<{ fileHandlerExecutorGetMetadataByTestRun: GraphqlFileMetadata[] }>({
						query: query,
						variables: { TestRunUid: testRunUid },
					});
				}),
				map(response => {
					const result = response.data?.fileHandlerExecutorGetMetadataByTestRun;
					return result?.length ? result.map(item => new FileMetadata(item)) : [];
				}),
			);
		}),
		shareReplay(1),
	);

	/**
	 * Following observables select specific file types from list of file metadata for current test run.
	 * Use to get information about specific files connected to test run.
	 */
	public xcomFiles$ = this.fileMetadata$.pipe(map(files => files.filter(file => file.FileType === TestFileType.Xcom)));

	public e2Files$ = this.fileMetadata$.pipe(map(files => files.filter(file => file.FileType === TestFileType.E2)));

	public atsFiles$ = this.fileMetadata$.pipe(map(files => files.filter(file => file.FileType === TestFileType.Ats)));

	public offboardFiles$ = this.fileMetadata$.pipe(map(files => files.filter(file => file.FileType === TestFileType.OffboardAgent || file.FileType === TestFileType.OffboardGadget)));

	public attachmentFiles$ = this.fileMetadata$.pipe(map(files => files.filter(file => file.FileType === TestFileType.TestCaseAttachment)));

	/**
	 * Constructor.
	 * @param apolloService To get data from graphql server.
	 * @param httpClient To get file from S3.
	 */
	constructor(
		private apolloService: ApolloService,
		private httpClient: HttpClient,
	) {}

	/**
	 * Get a file by its UID.
	 * @param fileUid UID of the file to get.
	 * @param fileName Name of file, to set properly on download.
	 * @returns File definition matching input parameters.
	 */
	public getFileByFileUid$(fileUid: string, fileName?: string): Observable<File | undefined> {
		const query = gql`
			query fileHandlerExecutorDownloadFile($FileUid: String!) {
				fileHandlerExecutorDownloadFile(FileUid: $FileUid)
			}
		`;

		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ fileHandlerExecutorDownloadFile: string }>({
					query: query,
					variables: {
						FileUid: fileUid,
					},
				});
			}),
			switchMap(response => {
				const file = response.data.fileHandlerExecutorDownloadFile;

				if (!file) {
					return of(undefined);
				}

				return this.httpClient
					.get(file, {
						responseType: 'blob',
					})
					.pipe(
						map(blob => {
							if (!blob) {
								return undefined;
							}
							return new File([blob], fileName ?? 'no-name');
						}),
					);
			}),
		);
	}

	/**
	 * Get ECU identification readouts (XCOM) for a test run from the legacy results database tables.
	 * Used as a fallback for old test runs whose readout data was extracted into the database
	 * instead of being stored as a file in S3.
	 * @param testRunUid UID of the test run to get readouts for.
	 * @returns List of readouts mapped to the EcuInformation display model, or an empty array.
	 */
	public getEcuReadoutsFromDb$(testRunUid: string): Observable<EcuInformation[]> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ resultsEcuReadoutsForTestRun: GraphqlEcuReadout[] }>({
					query: ECU_READOUTS_FOR_TEST_RUN_QUERY,
					variables: { testRunUid: testRunUid },
				});
			}),
			map(response => {
				const result = response.data?.resultsEcuReadoutsForTestRun;
				if (!result?.length) {
					return [];
				}
				// The database stores only the ECU, hex ID and value; there is no human-readable
				// description as there is in an XCOM file, so Description is left blank rather than
				// duplicating the ID (Name and EcuIdentification hold the same hex ID in the DB).
				return result.map(item => ({
					Ecu: item.ecuDdb,
					Id: item.name,
					Value: item.value,
					Description: '',
				}));
			}),
		);
	}

	/**
	 * Get E2 parameters for a test run from the legacy results database tables.
	 * Used as a fallback for old test runs whose E2 data was extracted into the database
	 * instead of being stored as a file in S3.
	 * @param testRunUid UID of the test run to get parameters for.
	 * @returns List of E2 parameters, or an empty array.
	 */
	public getEcuParamsFromDb$(testRunUid: string): Observable<EcuParam[]> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ resultsEcuParametersForTestRun: GraphqlEcuParam[] }>({
					query: ECU_PARAMETERS_FOR_TEST_RUN_QUERY,
					variables: { testRunUid: testRunUid },
				});
			}),
			map(response => {
				const result = response.data?.resultsEcuParametersForTestRun;
				if (!result?.length) {
					return [];
				}
				return result.map(item => ({
					TypeUid: item.typeUid,
					FileUid: item.fileUid,
					Name: item.name,
					Description: item.description,
					Value: item.value,
					EnumValue: item.enumValue,
					ValueType: item.valueType,
					EcuName: item.ecuName,
					EcuFamily: item.ecuFamily,
				}));
			}),
		);
	}

	/**
	 * Initializes service with current test run UID which is used to get linke files.
	 * @param testRunUid UID of current test run.
	 */
	public initFilesInformationForTestRun(testRunUid: string) {
		this.testRunSubject.next(testRunUid);
	}

	/**
	 * Get files for current test run run, filtered by files matching a specific test result, using the LinkUid of the result.
	 * @param linkUid Link UID to filter results by.
	 * @returns List of files matching conditions.
	 */
	public getFilesInformationForTestResult$(linkUid: string): Observable<FileMetadata[]> {
		return this.fileMetadata$.pipe(map(results => results.filter(item => item.LinkToTestItemUid === linkUid)));
	}

	public downloadFile(file: File): void {
		if (!file) {
			return;
		}
		const url = window.URL.createObjectURL(file);
		const a = document.createElement('a');
		document.body.appendChild(a);
		a.setAttribute('style', 'display: none');
		a.href = url;
		a.download = file.name;
		a.click();
		window.URL.revokeObjectURL(url);
		a.remove();
	}

	public getFilestringFromFile$(file: File): Observable<string> {
		if (!file.arrayBuffer) {
			return of('');
		}
		return from(file.arrayBuffer()).pipe(
			map(arrayBuffer => {
				const decoder = new TextDecoder('utf-8');
				return decoder.decode(arrayBuffer);
			}),
		);
	}
}
